import React from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Link from '@amzn/awsui-components-console/link';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { SUBMISSION_ACK_HEADER_KEY } from '../constants';
import { TRACK_MY_TRANSLATIONS_PATH } from 'src/common/routes';
import { INLINE_SLOT, splitAroundSlot } from 'src/i18n/inlineSlot';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface SubmissionAcknowledgementModalProps {
  visible: boolean;
  /** Group that was submitted — named in the acknowledgement. */
  stringGroupId: string;
  /** Version created by the submission; omitted only if the server returned none. */
  version?: number;
  /** Address the confirmation email will be sent to. */
  notificationEmail?: string;
  onDismiss: () => void;
  /** Client-side navigation to Track Translations; falls back to plain text when absent. */
  onTrackTranslationsClick?: () => void;
}

/**
 * The "acknowledge and release" step of the interim-period flow: shown the
 * moment a submission is accepted (HTTP 202), before TRANSCOM confirms it.
 *
 * Its whole job is to let the user walk away. It states the typical wait,
 * confirms it is safe to leave (the work continues server-side), and tells them
 * how they will be notified. It deliberately reports NO progress — the durable
 * pending banner on the page behind it, driven by server state, owns that.
 */
/** Advice copy, not a threshold anything measures against, but named so it has one home. */
const SUBMISSION_DURATION_MINUTES = 3;

const SubmissionAcknowledgementModal: React.FC<
  SubmissionAcknowledgementModalProps
> = ({
  visible,
  stringGroupId,
  version,
  notificationEmail,
  onDismiss,
  onTrackTranslationsClick,
}) => {
  const { t } = useTranslation();
  const trimmedEmail = notificationEmail?.trim();
  // Two whole-sentence resources rather than one with the version appended.
  const submittedSentence =
    version !== undefined
      ? t('sgd-ack-submitted-version', { group: stringGroupId, version })
      : t('sgd-ack-submitted', { group: stringGroupId });
  // User data must never appear in a resource file, so the address is always an ICU parameter.
  const emailSentence =
    trimmedEmail && trimmedEmail.length > 0
      ? t('sgd-ack-email-recipient', { recipient: trimmedEmail })
      : t('sgd-ack-email-recipient-you');
  const [beforeTrackLink, afterTrackLink] = splitAroundSlot(
    t('sgd-ack-check-status', { trackLink: INLINE_SLOT }),
  );

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={t(SUBMISSION_ACK_HEADER_KEY)}
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            {t('sgd-ack-got-it')}
          </Button>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {/* The group id is not emphasized inside this sentence: emphasis on part
            of a sentence cannot survive translation — the emphasized words move
            or disappear — so the whole sentence is one resource, per the
            static-copy-only rule in the localization design doc §5.2. */}
        <Alert type="success">{submittedSentence}</Alert>
        <Box variant="div">
          <ul>
            <li>
              {t('sgd-ack-duration', {
                minutes: SUBMISSION_DURATION_MINUTES,
              })}
            </li>
            <li>{t('sgd-ack-safe-to-leave')}</li>
            <li>{emailSentence}</li>
            <li>
              {beforeTrackLink}
              {onTrackTranslationsClick ? (
                <Link
                  href={TRACK_MY_TRANSLATIONS_PATH}
                  onFollow={(e) => {
                    e.preventDefault();
                    onTrackTranslationsClick();
                  }}
                >
                  {t('sgd-track-translations')}
                </Link>
              ) : (
                t('sgd-track-translations')
              )}
              {afterTrackLink}
            </li>
          </ul>
        </Box>
      </SpaceBetween>
    </Modal>
  );
};

export default SubmissionAcknowledgementModal;
